import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { WidgetActivityGroupCountsDocument } from "#app/lib/data";
// Direct module path (not the dashboards barrel — barrel → registry → definitions cycle).
import { scopeIsWorkflowBound } from "#app/lib/filters/issue-scope";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import type { Omittable } from "@salestrak/ui";

/** The viewer's zone, read once: a series token buckets in it, so a week
 *  starts where the viewer's week does. */
const VIEWER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

type UseActivityGroupCountsOptions = Omittable<{
  /** Server grouping token, e.g. `"stage.entered"` or `"actor"`. */
  groupBy: string;
  /** The scoped activity `where` from `useScopedActivityWhere` — the caller owns the fold. */
  where: Record<string, unknown> | undefined;
  skip?: boolean;
}>;

type UseActivityGroupCountsResult = Omittable<{
  /**
   * The answer in hand. The current variables' once `ready`; until then the
   * PREVIOUS variables' — Apollo drops `data` the moment the variables change,
   * and a consumer that keeps its shape through a re-query (a band that
   * skeletons only its figures, a member list that stands placeholder rows for
   * the rows it had) needs the last answer to keep it from. Empty before the
   * first answer and while the query is skipped.
   */
  counts: Record<string, number>;
  /** The current variables have answered. */
  ready: boolean;
  /** The query error, if any — a widget shows it as a `WidgetError` state. */
  error?: ErrorLike;
}>;

/**
 * Exact server-side activity counts grouped by a server token. The activity
 * fold (`useScopedActivityWhere`) narrows by team + actor + date; a workflow
 * dashboard additionally folds its pinned entity through `issueWhere` (activities
 * don't carry a workflow column — the server joins through the issue).
 */
export function useActivityGroupCounts({
  groupBy,
  where,
  skip = false,
}: UseActivityGroupCountsOptions): UseActivityGroupCountsResult {
  const scope = useDashboardScope();
  const issueWhere =
    scope?.entity && scopeIsWorkflowBound(scope.entity.type) ? scope.entityWhere : undefined;

  const variables = useMemo(
    () => ({ groupBy, where, issueWhere, timezone: VIEWER_TIMEZONE }),
    [groupBy, where, issueWhere],
  );

  const { data, previousData, error } = useQuery(WidgetActivityGroupCountsDocument, {
    variables,
    skip,
    fetchPolicy: "cache-and-network",
  });

  // A skipped query holds nothing: its previous answer was to a question no
  // longer asked. `previousData` is typed as a deep partial; it is a whole
  // result this hook received, so the cast restores the shape `data` has.
  const settled = data ?? (skip ? undefined : (previousData as typeof data));
  const counts = useMemo(() => {
    const record: Record<string, number> = {};
    if (settled?.activityGroupCounts) {
      for (const { key, total } of settled.activityGroupCounts) {
        record[key] = total;
      }
    }
    return record;
  }, [settled]);

  return { counts, ready: data?.activityGroupCounts !== undefined, error };
}
